import { useTranslation } from "react-i18next";
import { Clock } from "lucide-react";
import { Input } from "components/ui/input";
import { Badge } from "components/ui/badge";
import Select from "components/shared/inputs/Select";
import MoneyInput from "components/shared/inputs/MoneyInput";
import { useCompanyCurrency } from "utils/hooks/company";
import { cn } from "lib/utils";
import type { Shift, ShiftPatch } from "./types";

const errorClassName = "tw-border-red-500 focus-visible:tw-ring-red-500/20";

function FieldError({ message }: { message?: string }) {
    return message ? <span className="tw-text-red-500 tw-text-sm">{message}</span> : null;
}

interface ShiftDetailsProps {
    shift: Shift;
    errors?: any;
    onChange: (patch: ShiftPatch) => void;
}

function ShiftDetails({ shift, errors, onChange }: ShiftDetailsProps) {
    const { t } = useTranslation();
    const currency = useCompanyCurrency();

    return (
        <div className="tw-flex tw-flex-col tw-gap-1 tw-px-4 tw-pb-3 tw-pl-11">
            <div className="tw-flex tw-items-center tw-gap-2">
                <Select
                    value={shift.requestType}
                    onChange={(requestType) => onChange({ requestType: requestType ?? "open-shift" })}
                    showSearch={false}
                    className="tw-w-40 tw-shrink-0"
                >
                    <Select.Item value="open-shift">{t("open-shift")}</Select.Item>
                    <Select.Item value="call-off">{t("call-off")}</Select.Item>
                </Select>
                <Input
                    value={shift.reason}
                    onChange={(e) => onChange({ reason: e.target.value })}
                    placeholder={t("reason")}
                    className={cn(errors?.reason && errorClassName)}
                />
            </div>
            <FieldError message={errors?.reason?.message} />

            <div className="tw-flex tw-items-center tw-gap-2 tw-mt-1">
                <div className="tw-flex tw-items-center tw-gap-1.5 tw-shrink-0">
                    <span className="tw-text-sm tw-text-gray-600">{t("extra-pay")}</span>
                    <Badge
                        variant="outline"
                        className="tw-gap-1 tw-border-gray-300 tw-bg-gray-100 tw-text-gray-600 tw-font-medium"
                    >
                        <Clock size={12} />
                        {t("coming-soon")}
                    </Badge>
                </div>
                <Select
                    value={shift.payType}
                    onChange={(payType) => onChange({ payType: payType ?? "flat" })}
                    showSearch={false}
                    className="tw-w-40"
                    disabled
                >
                    <Select.Item value="flat">{t("flat-amount")}</Select.Item>
                    <Select.Item value="hourly">{t("per-hour")}</Select.Item>
                </Select>
                <MoneyInput
                    currency={currency}
                    value={shift.payAmount}
                    onValueChange={(payAmount) => onChange({ payAmount: payAmount ?? "" })}
                    placeholder={t("amount")}
                    className={cn("tw-w-32", errors?.payAmount && errorClassName)}
                    disabled
                />            </div>
            <FieldError message={errors?.payAmount?.message} />
        </div>
    );
}

export default ShiftDetails;
